import { Agent } from "model/agent";
import { GeographicRoute } from "model/geographic-route";
import { RoundRobinRoute } from "model/round-robin-route";
import { urls } from "routes/urls";

export type RoleTag = "Admin" | "Agent";
export type RoutingTag = "Round Robin" | "Geographic";

/**
 * Display label for a role tag. The "Agent" role is presented to users as "Lead Owner" for
 * consistency with the add/edit member flow; the underlying RoleTag value stays "Agent" so
 * filtering and sorting logic is unaffected.
 */
export const ROLE_TAG_LABELS: Record<RoleTag, string> = {
	Admin: "Admin",
	Agent: "Lead Owner",
};

/**
 * Role tags shown for a member. Admin when the member is an administrator; Agent when a
 * non-admin member is a billable lead owner. Non-admin, non-billable members have no role tag
 * (matches the design's empty cells).
 */
export const getRoleTags = (agent: Agent): RoleTag[] => {
	const tags: RoleTag[] = [];
	if (agent.admin) {
		tags.push("Admin");
	}
	if (!agent.admin && agent.ownsLeads) {
		tags.push("Agent");
	}
	return tags;
};

/**
 * Routing tags shown for a member, consolidating the previous Round Robin and Geographic
 * Routing columns. Zip codes are intentionally not shown here (managed via Lead Routing).
 */
export const getRoutingTags = (
	agent: Agent,
	roundRobinRoutes: RoundRobinRoute[],
	geographicRoutes: GeographicRoute[]
): RoutingTag[] => {
	const tags: RoutingTag[] = [];
	if (roundRobinRoutes.some((route) => route.agent.id === agent.id)) {
		tags.push("Round Robin");
	}
	if (geographicRoutes.some((route) => route.agent.id === agent.id)) {
		tags.push("Geographic");
	}
	return tags;
};

/**
 * Member name as shown in the table, with the suffixes the design appends inline: "(Account
 * Owner)" for the account owner and "(You)" for the logged-in member. Both can apply at once —
 * the owner viewing their own row sees "Piper Doe (Account Owner) (You)".
 */
export const getMemberDisplayName = (agent: Agent, loggedInAgentId?: number): string => {
	const suffixes: string[] = [];
	if (agent.isAccountOwner) {
		suffixes.push("(Account Owner)");
	}
	if (loggedInAgentId !== undefined && agent.id === loggedInAgentId) {
		suffixes.push("(You)");
	}
	return [agent.label, ...suffixes].join(" ");
};

/**
 * Where a member row links. The logged-in member goes to their own Profile page rather than the
 * member detail view, so there is one place to manage your own information.
 */
export const getMemberUrl = (agent: Agent, loggedInAgentId?: number): string =>
	loggedInAgentId !== undefined && agent.id === loggedInAgentId
		? urls.settingsProfile
		: urls.agentProfile(agent.id);

/** `?billable=1` from the Account page's "View List": the members counted as Billable Lead Owners. */
export const isBillableFilterParam = (value: unknown): boolean => {
	const raw = Array.isArray(value) ? value[0] : value;
	return raw === "1" || raw === "true";
};

/** The same predicate as the table's Billable header count, so the filtered list matches it. */
export const matchesBillableFilter = (agent: Agent, billable: boolean): boolean =>
	!billable || !!agent.ownsLeads;
